import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import type { Instrument } from '../lib/types';
import { Button, Card, CardHeader, ErrorText, Field, Input, PageHeader, Pill, Select } from '../components/ui';

interface UserRow {
  id: string;
  email: string;
  name: string;
  status: string;
  roles: string[];
  lastLoginAt: string | null;
  totpEnabled: boolean;
}
interface AccountRow {
  id: string;
  code: string;
  name: string;
  currency: string;
  market: { id: string; code: string };
  traders: { id: string; name: string }[];
}

function Ok({ text }: { text: string | null }) {
  return text ? <p className="m-0 text-sm text-up">{text}</p> : null;
}

const ROLE_LABELS: Record<string, string> = { TRADER: 'Trader', MASTER_TRADER: 'Master Trader', RISK_MANAGER: 'Risk Manager', ADMIN: 'Admin' };

/** Admin: change someone's roles (PUT /users/:id/roles). Removing a role signs them out everywhere; the change is audited. */
function RoleEditor({ user, onDone }: { user: UserRow; onDone: (msg: string) => void }) {
  const qc = useQueryClient();
  const [roles, setRoles] = useState<string[]>(user.roles);
  const save = useMutation({
    mutationFn: () => api<{ changed: boolean; signedOut?: boolean }>(`/users/${user.id}/roles`, { method: 'PUT', body: { roles } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      onDone(!r.changed ? 'No change.' : r.signedOut ? `Roles saved. ${user.name} was signed out so the removed role stops working.` : 'Roles saved.');
    },
  });
  return (
    <fieldset className="m-0 flex flex-col gap-2 rounded-lg border border-line2 bg-raised p-3">
      <legend className="px-1 text-sm font-semibold">Roles for {user.name}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(ROLE_LABELS).map(([role, label]) => (
          <label key={role} className="flex min-h-11 cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" checked={roles.includes(role)} onChange={(e) => setRoles(e.target.checked ? [...roles, role] : roles.filter((r) => r !== role))} />
            {label}
          </label>
        ))}
      </div>
      <ErrorText error={save.error ? errorText(save.error) : null} />
      <div className="flex gap-2">
        <Button variant="primary" onClick={() => save.mutate()} disabled={!roles.length || save.isPending}>Save roles</Button>
        <Button onClick={() => onDone('')}>Cancel</Button>
      </div>
    </fieldset>
  );
}

interface ResetLink {
  id: string;
  email: string;
  name: string;
  url: string;
  expiresAt: string;
  minutes: number;
  emailed: boolean;
  self: boolean;
}

/**
 * INT-ENR-05: an Admin makes a one-time password reset link. It's shown once here so it works without email; the
 * Admin never sees or sets the password, and the member still needs their authenticator app.
 */
function ResetLinkBox({ user, tz, onClose }: { user: UserRow; tz: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const make = useMutation({ mutationFn: () => api<ResetLink>(`/users/${user.id}/password-reset-link`, { method: 'POST' }) });
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[#22385a] bg-accent-bg p-3">
      {!make.data ? (
        <>
          <span className="text-sm">
            Make a one-time link for <b>{user.name}</b> to choose a new password? It replaces any earlier reset link. Saving the new password signs them out everywhere; they still need their authenticator app.
          </span>
          <ErrorText error={make.error ? errorText(make.error) : null} />
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => make.mutate()} disabled={make.isPending}>Make reset link</Button>
            <Button onClick={onClose}>Cancel</Button>
          </div>
        </>
      ) : (
        <>
          <span className="text-sm font-semibold">Reset link for {make.data.name} — copy it now, it won’t be shown again</span>
          <div className="flex gap-2">
            <Input aria-label="Reset link" readOnly value={make.data.url} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
            <Button onClick={() => void copy(make.data.url)}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
          <span className="text-xs text-sub">
            Works once, until {fmtTime(make.data.expiresAt, tz, true)}.{' '}
            {make.data.emailed ? `We also emailed it to ${make.data.email}.` : 'Email isn’t set up on this server, so pass it on yourself (privately).'}
          </span>
          <Button className="self-start" onClick={onClose}>Done</Button>
        </>
      )}
    </div>
  );
}

export function Admin() {
  const me = useMe();
  const qc = useQueryClient();
  const isAdmin = hasRole(me.data?.roles, 'ADMIN');
  const tz = me.data?.timezone ?? 'UTC';
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserRow[]>('/users') });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api<AccountRow[]>('/accounts') });
  const markets = useQuery({ queryKey: ['markets'], queryFn: () => api<{ id: string; code: string; currency: string }[]>('/markets') });
  const instruments = useQuery({ queryKey: ['instruments', 'all'], queryFn: () => api<Instrument[]>('/instruments') });
  const traders = (users.data ?? []).filter((u) => u.roles.includes('TRADER'));
  const inv = (...k: string[]) => k.forEach((x) => void qc.invalidateQueries({ queryKey: [x] }));

  const [nu, setNu] = useState({ email: '', name: '', role: 'TRADER', timezone: 'Asia/Karachi', initialPassword: '' });
  const createUser = useMutation({ mutationFn: () => api<{ id: string }>('/users', { body: { ...nu, roles: [nu.role] } }), onSuccess: () => { inv('users'); setNu({ ...nu, email: '', name: '', initialPassword: '' }); } });
  const [na, setNa] = useState({ code: '', name: '', marketId: '', currency: '', startingEquity: '' });
  const createAccount = useMutation({ mutationFn: () => api('/accounts', { body: { ...na, marketId: na.marketId || markets.data?.[0]?.id, currency: na.currency || markets.data?.find((m) => m.id === (na.marketId || markets.data?.[0]?.id))?.currency } }), onSuccess: () => inv('accounts') });
  const [assign, setAssign] = useState({ accountId: '', userId: '' });
  const assignAccount = useMutation({ mutationFn: () => api(`/accounts/${assign.accountId}/assign`, { body: { userId: assign.userId } }), onSuccess: () => inv('accounts') });
  const [sym, setSym] = useState<{ userId: string; ids: string[] }>({ userId: '', ids: [] });
  const [editing, setEditing] = useState<string | null>(null);
  const [roleMsg, setRoleMsg] = useState<{ id: string; text: string } | null>(null);
  const [resetFor, setResetFor] = useState<string | null>(null);
  const assignSymbols = useMutation({ mutationFn: () => api<{ added: number }>('/symbol-assignments', { body: { userId: sym.userId, instrumentIds: sym.ids } }) });

  return (
    <>
      <PageHeader title="Admin" sub="People, roles, accounts and symbols · FX rates have their own screen" />
      <div className="grid gap-3.5 xl:grid-cols-2">
        {isAdmin && (
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">Add a person</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} /></Field>
              <Field label="Email"><Input type="email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} /></Field>
              <Field label="Role">
                <Select value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}>
                  <option value="TRADER">Trader</option>
                  <option value="MASTER_TRADER">Master Trader</option>
                  <option value="RISK_MANAGER">Risk Manager</option>
                  <option value="ADMIN">Admin</option>
                </Select>
              </Field>
              <Field label="Timezone"><Input value={nu.timezone} onChange={(e) => setNu({ ...nu, timezone: e.target.value })} /></Field>
              <Field label="Initial password" hint="12+ characters; they set up 2-step sign-in on first login" className="col-span-2">
                <Input type="password" autoComplete="new-password" value={nu.initialPassword} onChange={(e) => setNu({ ...nu, initialPassword: e.target.value })} />
              </Field>
            </div>
            <ErrorText error={createUser.error ? errorText(createUser.error) : null} />
            <Ok text={createUser.isSuccess ? 'Person added.' : null} />
            <Button variant="primary" className="self-start" onClick={() => createUser.mutate()} disabled={createUser.isPending}>Add person</Button>
          </Card>
        )}

        <Card>
          <CardHeader title={`People · ${users.data?.length ?? 0}`} />
          <div className="max-h-[420px] overflow-y-auto">
            {users.data?.map((u) => (
              <div key={u.id} className="flex flex-col gap-2 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-col">
                    <span className="font-semibold">{u.name}</span>
                    <span className="text-xs text-muted">{u.email} · last sign-in {u.lastLoginAt ? fmtTime(u.lastLoginAt, tz, true) : 'never'}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {u.roles.map((r) => <Pill key={r} tone="accent">{ROLE_LABELS[r] ?? r}</Pill>)}
                    {!u.totpEnabled && <Pill tone="warn">2FA not set</Pill>}
                    {u.status !== 'ACTIVE' && <Pill tone="down">{u.status.toLowerCase()}</Pill>}
                    {isAdmin && editing !== u.id && (
                      <button type="button" aria-label={`Change roles for ${u.name}`} onClick={() => { setEditing(u.id); setRoleMsg(null); }} className="min-h-9 cursor-pointer rounded-lg px-2 text-sm text-accent">
                        Roles
                      </button>
                    )}
                    {isAdmin && u.status === 'ACTIVE' && resetFor !== u.id && (
                      <button type="button" aria-label={`Reset link for ${u.name}`} onClick={() => setResetFor(u.id)} className="min-h-9 cursor-pointer rounded-lg px-2 text-sm text-accent">
                        Reset link
                      </button>
                    )}
                  </div>
                </div>
                {editing === u.id && <RoleEditor user={u} onDone={(msg) => { setEditing(null); setRoleMsg(msg ? { id: u.id, text: msg } : null); }} />}
                {roleMsg?.id === u.id && <Ok text={roleMsg.text} />}
                {resetFor === u.id && <ResetLinkBox user={u} tz={tz} onClose={() => setResetFor(null)} />}
              </div>
            ))}
          </div>
        </Card>

        {isAdmin && (
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">New trading account</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code"><Input value={na.code} onChange={(e) => setNa({ ...na, code: e.target.value })} placeholder="FX-14" /></Field>
              <Field label="Name"><Input value={na.name} onChange={(e) => setNa({ ...na, name: e.target.value })} /></Field>
              <Field label="Market">
                <Select value={na.marketId} onChange={(e) => setNa({ ...na, marketId: e.target.value, currency: markets.data?.find((m) => m.id === e.target.value)?.currency ?? '' })}>
                  {markets.data?.map((m) => <option key={m.id} value={m.id}>{m.code}</option>)}
                </Select>
              </Field>
              <Field label="Currency"><Input value={na.currency} onChange={(e) => setNa({ ...na, currency: e.target.value.toUpperCase() })} placeholder="USD" /></Field>
              <Field label="Starting equity" className="col-span-2"><Input mono inputMode="decimal" value={na.startingEquity} onChange={(e) => setNa({ ...na, startingEquity: e.target.value })} /></Field>
            </div>
            <ErrorText error={createAccount.error ? errorText(createAccount.error) : null} />
            <Ok text={createAccount.isSuccess ? 'Account created with the default risk policy.' : null} />
            <Button variant="primary" className="self-start" onClick={() => createAccount.mutate()} disabled={createAccount.isPending}>Create account</Button>

            <h3 className="m-0 mt-2 text-sm font-semibold">Assign an account</h3>
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <Select aria-label="Account" value={assign.accountId} onChange={(e) => setAssign({ ...assign, accountId: e.target.value })}>
                <option value="">Account…</option>
                {accounts.data?.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.traders.map((t) => t.name).join(', ') || 'unassigned'}</option>)}
              </Select>
              <Select aria-label="Trader" value={assign.userId} onChange={(e) => setAssign({ ...assign, userId: e.target.value })}>
                <option value="">Trader…</option>
                {traders.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
              <Button onClick={() => assignAccount.mutate()} disabled={!assign.accountId || !assign.userId}>Assign</Button>
            </div>
            <ErrorText error={assignAccount.error ? errorText(assignAccount.error) : null} />
          </Card>
        )}

        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">Assign symbols</h2>
          <Select aria-label="Trader" value={sym.userId} onChange={(e) => setSym({ ...sym, userId: e.target.value })}>
            <option value="">Trader…</option>
            {traders.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
          <div className="flex max-h-[220px] flex-wrap gap-1.5 overflow-y-auto">
            {instruments.data?.map((i) => {
              const on = sym.ids.includes(i.id);
              return (
                <button key={i.id} type="button" aria-pressed={on} onClick={() => setSym({ ...sym, ids: on ? sym.ids.filter((x) => x !== i.id) : [...sym.ids, i.id] })} className={`min-h-9 cursor-pointer rounded-full px-3 py-1 text-sm ${on ? 'bg-accent font-semibold text-bg' : 'border border-line2 bg-raised text-sub'}`}>
                  {i.symbol}
                </button>
              );
            })}
          </div>
          <ErrorText error={assignSymbols.error ? errorText(assignSymbols.error) : null} />
          <Ok text={assignSymbols.data ? `${assignSymbols.data.added} symbol(s) added.` : null} />
          <Button variant="primary" className="self-start" onClick={() => assignSymbols.mutate()} disabled={!sym.userId || !sym.ids.length}>Assign selected</Button>
        </Card>

      </div>
    </>
  );
}
